import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocket, useSocketEvent } from '../context/SocketContext';
import { StatusBadge } from '../components/Badges';
import PatientDetailModal from '../components/PatientDetailModal';
import ConfirmDialog from '../components/ConfirmDialog';
import NowServingDisplay from '../components/NowServingDisplay';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Users, Clock, CheckCircle2, IndianRupee, Upload, FileText, Image as ImageIcon, Trash2, Plus, X, Wifi, WifiOff } from 'lucide-react';

export default function DoctorDashboard() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const { showToast } = useToast();
  const { user } = useAuth();
  const [showStats, setShowStats] = React.useState(() => localStorage.getItem('dashboard_showStats') !== 'false');
  const toggleStats = () => setShowStats(v => { localStorage.setItem('dashboard_showStats', !v); return !v; });
  const { connected } = useSocket();

  const loadAll = useCallback(async () => {
    try {
      const [statsRes, queueRes, itemsRes] = await Promise.all([
        api.get('/patients/stats'),
        api.get('/patients/queue'),
        api.get('/dashboard/items'),
      ]);
      setStats(statsRes.data);
      setQueue(queueRes.data.patients);
      setDashboardItems(itemsRes.data.items);
    } catch (err) {
      showToast('Could not load dashboard data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadAll();
    // Safety-net poll - real-time socket events below handle the normal
    // case instantly; this just guards against a dropped connection.
    const interval = setInterval(loadAll, 60000);
    return () => clearInterval(interval);
  }, [loadAll]);

  // Real-time: any token created, status changed, or patient archived by
  // either the doctor or receptionist - in this tab or any other - updates
  // this dashboard immediately, including after a daily reset.
  useSocketEvent('queue:updated', () => loadAll());
  useSocketEvent('queue:reset', () => loadAll());
  useSocketEvent('dashboard:item-added', (payload) => {
    if (payload?.item) setDashboardItems((prev) => [payload.item, ...prev.filter((i) => i._id !== payload.item._id)]);
  });
  useSocketEvent('dashboard:item-deleted', (payload) => {
    if (payload?.id) setDashboardItems((prev) => prev.filter((i) => i._id !== payload.id));
  });

  function handlePatientUpdated(updated) {
    setQueue((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
  }

  async function confirmDeleteItem() {
    if (!deleteItemTarget) return;
    try {
      await api.delete(`/dashboard/items/${deleteItemTarget._id}`);
      showToast('Dashboard item deleted.', 'success');
      setDashboardItems((prev) => prev.filter((i) => i._id !== deleteItemTarget._id));
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete item.', 'error');
    } finally {
      setDeleteItemTarget(null);
    }
  }

  const servingNow = queue.filter((p) => p.status === 'in-progress');
  const waiting = queue.filter((p) => p.status === 'waiting');

  return (
    <AppLayout title="Doctor Dashboard" subtitle="Live overview of today's clinic activity">
      <div
        title={connected ? 'Live updates connected' : 'Live updates disconnected — reconnecting...'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 600,
          color: connected ? 'var(--color-success)' : 'var(--color-warning)',
          marginBottom: 16,
        }}
      >
        {connected ? <Wifi size={15} /> : <WifiOff size={15} />}
        {connected ? 'Live — updates appear instantly' : 'Reconnecting to live updates...'}
      </div>

      <div style={{ marginBottom: 20 }}>
        <NowServingDisplay showNextButton onAdvanced={() => loadAll()} />
      </div>

      {user?.isAdmin && (
        <button className="btn btn-outline btn-sm" onClick={toggleStats}
          style={{ marginBottom:8, display:'flex', alignItems:'center', gap:6, width:'fit-content' }}>
          {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
        </button>
      )}
      {(showStats || !user?.isAdmin) && (
      <div className="stat-grid">
        <StatCard
          icon={Users}
          color="primary"
          label="Today's Patients (total)"
          value={loading ? '—' : stats?.todayTotal ?? 0}
        />
        <StatCard
          icon={Clock}
          color="warning"
          label="Waiting"
          value={loading ? '—' : stats?.statusCounts?.waiting ?? 0}
        />
        <StatCard icon={CheckCircle2} color="success" label="Completed" value={loading ? '—' : stats?.statusCounts?.completed ?? 0} />
        {user?.isAdmin && <StatCard icon={IndianRupee} color="info" label="Revenue (7 days)" value={loading ? '—' : `₹${(stats?.totalRevenue7Days ?? 0).toLocaleString('en-IN')}`} />}
      </div>
      )}

      {!loading && stats?.todayArchived > 0 && (
        <div
          className="card"
          style={{
            padding: '10px 16px',
            marginBottom: 20,
            fontSize: 13,
            color: 'var(--color-ink-soft)',
            background: 'var(--color-info-soft)',
            border: 'none',
          }}
        >
          {stats.todayArchived} of today's {stats.todayTotal} patient{stats.todayTotal === 1 ? '' : 's'} {stats.todayArchived === 1 ? 'has' : 'have'} been cleared from the active queue (e.g. via Reset Daily Tokens). {stats.todayActive} still active. All records remain in "All Patients."
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(280px,1fr)', gap: 20, marginBottom: 24 }}>
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 16px' }}>Patients — Last 7 Days</h3>
          {stats?.trend && (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={stats.trend}>
                <defs>
                  <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }}
                />
                <Area type="monotone" dataKey="value" stroke="var(--color-primary)" strokeWidth={2} fill="url(#colorTrend)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 16px' }}>Today's Status Breakdown</h3>
          {stats?.statusCounts && (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[
                  { label: 'Waiting', value: stats.statusCounts.waiting },
                  { label: 'In Progress', value: stats.statusCounts['in-progress'] },
                  { label: 'Completed', value: stats.statusCounts.completed },
                  { label: 'Cancelled', value: stats.statusCounts.cancelled },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }}
                />
                <Bar dataKey="value" fill="var(--color-accent)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Currently Serving</h3>
        {servingNow.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: 13 }}>
            No patient is currently in progress.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {servingNow.map((p) => (
              <div key={p._id} className="card" style={{ padding: 16, cursor: 'pointer' }} onClick={() => setSelectedPatient(p)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>
                      Token #{p.tokenNumber} · {p.patientUID}
                    </div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                {p.reasonForVisit && <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', marginTop: 8 }}>{p.reasonForVisit}</div>}
              </div>
            ))}
          </div>
        )}
        {waiting.length > 0 && (
          <p style={{ fontSize: 13, color: 'var(--color-ink-faint)', marginTop: 10 }}>
            {waiting.length} more patient{waiting.length !== 1 ? 's' : ''} waiting in queue.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Dashboard Files & Reports</h3>
        <button className="btn btn-outline btn-sm" onClick={() => setUploadModalOpen(true)}>
          <Plus size={14} />
          Add File
        </button>
      </div>

      {dashboardItems.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: 13 }}>
          No files added yet. Upload images or PDFs (like X-rays or reports) along with optional chart data.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {dashboardItems.map((item) => (
            <DashboardItemCard key={item._id} item={item} onDelete={() => setDeleteItemTarget(item)} />
          ))}
        </div>
      )}

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          editable
          onClose={() => setSelectedPatient(null)}
          onUpdated={(updated) => {
            handlePatientUpdated(updated);
            setSelectedPatient(updated);
          }}
        />
      )}

      {uploadModalOpen && (
        <UploadDashboardItemModal
          onClose={() => setUploadModalOpen(false)}
          onUploaded={(item) => {
            setDashboardItems((prev) => [item, ...prev]);
            setUploadModalOpen(false);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteItemTarget}
        title="Delete this file?"
        message={`"${deleteItemTarget?.title}" will be permanently removed from the dashboard.`}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDeleteItem}
        onCancel={() => setDeleteItemTarget(null)}
      />
    </AppLayout>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-card-icon" style={{ background: `var(--color-${color}-soft)` }}>
        <Icon size={18} color={`var(--color-${color})`} />
      </div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

function DashboardItemCard({ item, onDelete }) {
  const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {item.fileType === 'pdf' ? <FileText size={18} color="var(--color-danger)" /> : <ImageIcon size={18} color="var(--color-info)" />}
          <span style={{ fontWeight: 700, fontSize: 14 }}>{item.title}</span>
        </div>
        <button className="btn btn-icon btn-ghost" onClick={onDelete} aria-label="Delete">
          <Trash2 size={15} color="var(--color-danger)" />
        </button>
      </div>
      {item.description && <p style={{ fontSize: 12, color: 'var(--color-ink-soft)', margin: '0 0 10px' }}>{item.description}</p>}

      {item.fileType === 'image' ? (
        <a href={`${apiOrigin}${item.filePath}`} target="_blank" rel="noopener noreferrer">
          <img src={`${apiOrigin}${item.filePath}`} alt={item.title} style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
        </a>
      ) : (
        <a href={`${apiOrigin}${item.filePath}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ width: '100%' }}>
          <FileText size={14} />
          Open PDF
        </a>
      )}

      {item.graphData && item.graphData.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={item.graphData}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} />
              <Bar dataKey="value" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function UploadDashboardItemModal({ onClose, onUploaded }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [graphRows, setGraphRows] = useState([{ label: '', value: '' }]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  function updateGraphRow(idx, field, value) {
    setGraphRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }
  function addGraphRow() {
    setGraphRows((rows) => [...rows, { label: '', value: '' }]);
  }
  function removeGraphRow(idx) {
    setGraphRows((rows) => rows.filter((_, i) => i !== idx));
  }

  async function handleUpload() {
    if (!title.trim()) {
      showToast('Please enter a title.', 'error');
      return;
    }
    if (!file) {
      showToast('Please choose an image or PDF file.', 'error');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    const validGraphData = graphRows
      .filter((r) => r.label.trim() && r.value !== '')
      .map((r) => ({ label: r.label, value: Number(r.value) }));
    if (validGraphData.length > 0) {
      formData.append('graphData', JSON.stringify(validGraphData));
    }

    try {
      const res = await api.post('/dashboard/items', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast('File added to dashboard.', 'success');
      onUploaded(res.data.item);
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 22px 0' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Add Dashboard File</h2>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 22 }}>
          <div className="field">
            <label className="label">Title *</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekly Patient Report" />
          </div>
          <div className="field">
            <label className="label">Description</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional short note" />
          </div>
          <div className="field">
            <label className="label">File (Image or PDF) *</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="input"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>

          <div className="field">
            <label className="label">Graph Data (optional)</label>
            <p style={{ fontSize: 12, color: 'var(--color-ink-faint)', margin: '0 0 8px' }}>
              Add labeled values to show as a small chart on this card, e.g. "Mon" / 12
            </p>
            {graphRows.map((row, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  className="input"
                  placeholder="Label"
                  value={row.label}
                  onChange={(e) => updateGraphRow(idx, 'label', e.target.value)}
                />
                <input
                  className="input"
                  type="number"
                  placeholder="Value"
                  value={row.value}
                  onChange={(e) => updateGraphRow(idx, 'value', e.target.value)}
                />
                <button className="btn btn-icon btn-ghost" onClick={() => removeGraphRow(idx)}>
                  <X size={14} />
                </button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={addGraphRow}>
              <Plus size={14} />
              Add row
            </button>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={handleUpload} disabled={uploading}>
            <Upload size={16} />
            {uploading ? 'Uploading...' : 'Upload to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
}
